import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface PlatformFeature {
  title: string;
  description: string;
}

interface PlatformCardProps {
  name: string;
  tagline: string;
  description: string;
  image: string;
  imageAlt: string;
  features: PlatformFeature[];
  primaryColor: string;
  onLearnMore: () => void;
}

const PlatformCard = ({
  name,
  tagline,
  description,
  image,
  imageAlt,
  features,
  primaryColor,
  onLearnMore,
}: PlatformCardProps) => {
  return (
    <div className="bg-card rounded-xl shadow-subtle overflow-hidden border border-border hover:shadow-authority transition-all duration-300">
      <div className="relative h-64 overflow-hidden">
        <AppImage
          src={image}
          alt={imageAlt}
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 bg-gradient-to-t from-${primaryColor} to-transparent opacity-60`}></div>
      </div>
      
      <div className="p-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className={`w-12 h-12 rounded-lg bg-${primaryColor} bg-opacity-10 flex items-center justify-center`}>
            <Icon name="CubeIcon" size={24} className={`text-${primaryColor}`} />
          </div>
          <div>
            <h3 className="text-2xl font-display font-semibold text-secondary">{name}</h3>
            <p className="text-sm text-muted-foreground">{tagline}</p>
          </div>
        </div>
        
        <p className="text-foreground mb-6 leading-relaxed">{description}</p>
        
        <div className="space-y-3 mb-6">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start space-x-3">
              <Icon name="CheckCircleIcon" size={20} className="text-accent mt-0.5 flex-shrink-0" variant="solid" />
              <div>
                <p className="font-medium text-foreground">{feature.title}</p>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
        
        <button
          onClick={onLearnMore}
          className="w-full py-3 px-6 bg-secondary text-secondary-foreground font-display font-medium rounded-lg hover:shadow-authority transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center space-x-2"
        >
          <span>Explore Platform</span>
          <Icon name="ArrowRightIcon" size={20} />
        </button>
      </div>
    </div>
  );
};

export default PlatformCard;